import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { siteInfo } from "../mock";
import { getActions, getNews, getDelegates } from "../api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Users,
  Megaphone,
  Newspaper,
  UserPlus,
  ChevronRight,
  ArrowRight,
  Shield,
  Handshake,
  Scale,
} from "lucide-react";

const features = [
  {
    icon: Shield,
    title: "Protection",
    description: "Nous défendons vos droits et intérêts au quotidien.",
  },
  {
    icon: Handshake,
    title: "Accompagnement",
    description: "Un suivi personnalisé pour chaque salarié.",
  },
  {
    icon: Scale,
    title: "Équité",
    description: "Nous luttons pour des conditions de travail justes.",
  },
];

const sections = [
  {
    icon: Users,
    title: "Nous connaître",
    description: "Découvrez notre équipe et nos valeurs",
    path: "/nous-connaitre",
    color: "bg-sky-50 text-sky-600",
  },
  {
    icon: Megaphone,
    title: "Nos actions",
    description: "Nos combats pour vos droits",
    path: "/nos-actions",
    color: "bg-amber-50 text-amber-600",
  },
  {
    icon: Newspaper,
    title: "Notre actualité",
    description: "Les dernières nouvelles syndicales",
    path: "/notre-actualite",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: UserPlus,
    title: "Nous rejoindre",
    description: "Devenez adhérent et faites entendre votre voix",
    path: "/nous-rejoindre",
    color: "bg-rose-50 text-rose-600",
  },
];

export default function HomePage() {
  const [latestAction, setLatestAction] = useState(null);
  const [latestNews, setLatestNews] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [actionsData, newsData] = await Promise.all([
          getActions(),
          getNews(),
        ]);
        if (actionsData.length > 0) setLatestAction(actionsData[0]);
        if (newsData.length > 0) setLatestNews(newsData[0]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-sky-50/40 to-white">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-sky-100/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1 space-y-8 text-center lg:text-left">
              <Badge className="bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-100 px-4 py-1.5 text-xs font-semibold">
                Section syndicale UNSA
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight">
                Ensemble,{" "}
                <span className="text-sky-500">défendons</span>
                <br />
                vos droits
              </h1>
              <p className="text-lg text-slate-500 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {siteInfo.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/nous-rejoindre"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-sky-500 text-white font-semibold rounded-xl hover:bg-sky-600 transition-colors duration-200 shadow-lg shadow-sky-200/50"
                >
                  Nous rejoindre
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/nous-connaitre"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-slate-700 font-semibold rounded-xl border border-slate-200 hover:border-sky-300 hover:text-sky-600 transition-colors duration-200"
                >
                  En savoir plus
                </Link>
              </div>
            </div>

            <div className="flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-sky-200/30 rounded-full blur-2xl scale-110" />
                <img
                  src={siteInfo.logo}
                  alt={siteInfo.name}
                  className="relative w-56 h-56 md:w-72 md:h-72 object-contain drop-shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex items-start gap-4 p-6 rounded-2xl bg-slate-50/50 hover:bg-sky-50/50 transition-colors duration-300 group"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center group-hover:bg-sky-200 transition-colors duration-300">
                  <feature.icon className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Navigation Sections */}
      <section className="py-16 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-3">
              Découvrez notre section
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Explorez nos différentes rubriques pour mieux nous connaître et
              suivre nos actions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sections.map((section) => (
              <Link key={section.path} to={section.path} className="group">
                <Card className="h-full border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-white group-hover:-translate-y-1">
                  <CardContent className="p-6 flex flex-col items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${section.color}`}
                    >
                      <section.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1 group-hover:text-sky-600 transition-colors">
                        {section.title}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {section.description}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-sky-500 group-hover:translate-x-1 transition-all duration-300 mt-auto" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Content Preview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Latest Action */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-800">Dernière action</h2>
                <Link
                  to="/nos-actions"
                  className="text-sm text-sky-500 hover:text-sky-600 font-medium flex items-center gap-1 transition-colors"
                >
                  Voir tout <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              {loading ? (
                <Card className="overflow-hidden border-0 shadow-sm">
                  <Skeleton className="aspect-video w-full" />
                  <CardContent className="p-5 space-y-3">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ) : latestAction ? (
                <Link to={`/nos-actions/${latestAction.id}`} className="group block">
                  <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={latestAction.image}
                        alt={latestAction.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-5">
                      <p className="text-xs text-sky-500 font-medium mb-2">
                        {new Date(latestAction.date).toLocaleDateString("fr-FR", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                      <h3 className="font-semibold text-slate-800 mb-2 group-hover:text-sky-600 transition-colors">
                        {latestAction.title}
                      </h3>
                      <p className="text-sm text-slate-500 line-clamp-2">
                        {latestAction.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ) : null}
            </div>

            {/* Latest News */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-800">Dernière actualité</h2>
                <Link
                  to="/notre-actualite"
                  className="text-sm text-sky-500 hover:text-sky-600 font-medium flex items-center gap-1 transition-colors"
                >
                  Voir tout <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              {loading ? (
                <Card className="overflow-hidden border-0 shadow-sm">
                  <Skeleton className="aspect-video w-full" />
                  <CardContent className="p-5 space-y-3">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ) : latestNews ? (
                <Link to={`/notre-actualite/${latestNews.id}`} className="group block">
                  <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={latestNews.image}
                        alt={latestNews.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-5">
                      <p className="text-xs text-sky-500 font-medium mb-2">
                        {new Date(latestNews.date).toLocaleDateString("fr-FR", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                      <h3 className="font-semibold text-slate-800 mb-2 group-hover:text-sky-600 transition-colors">
                        {latestNews.title}
                      </h3>
                      <p className="text-sm text-slate-500 line-clamp-2">
                        {latestNews.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">
            Rejoignez-nous dès aujourd'hui
          </h2>
          <p className="text-slate-500 mb-8 max-w-2xl mx-auto">
            Ensemble, nous sommes plus forts. Rejoignez l'UNSA AEGIDE DOMITYS
            et participez à l'amélioration de vos conditions de travail.
          </p>
          <Link
            to="/nous-rejoindre"
            className="inline-flex items-center gap-2 px-8 py-4 bg-sky-500 text-white font-semibold rounded-xl hover:bg-sky-600 transition-colors duration-200 shadow-lg shadow-sky-200/50"
          >
            Devenir adhérent
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
